/* eslint-disable max-len */
import {  useLang } from '@/common/utils/i18n'


interface IQuestion {
  title: string;
  subQuestions: {
    title: string;
    anser: string;
  }[];
}


const questionsEn: IQuestion[] = [
  {
    title: 'Account and Registration Issues',
    subQuestions: [
      {
        title: 'How can I register and create a corporate account?',
        anser: 'If you need to register and create corporate accounts for your company, please click [Contact Us], and we will provide you with corporate services and relevant guidance.',
      },
      {
        title: 'How can corporate account administrators manage their company accounts?',
        anser: 'Administrators can use their admin account to manage operations, such as adding or deleting corporate accounts through the portal, and overseeing various types of information associated with corporate accounts.',
      },
      {
        title: 'Can I use multiple accounts (personal and corporate accounts) at the same time?',
        anser: 'We support the simultaneous use of multiple accounts, but you need to differentiate the login credentials for each account. The same phone number can only be linked to only one account. If you have already registered a personal account with your phone number, you will receive an error message when trying to bind or rebind it to a corporate account.<br/>Therefore, we recommend using your corporate email to register and log in to your corporate account, while using your phone number and other information to log in to your personal account. However, when placing an order, you can still enter your phone number to allow the delivery personnel to contact you.',
      },
    ],
  },
  {
    title: 'Order and Delivery Issues',
    subQuestions: [
      {
        title: 'How can I place an order? Can I modify or cancel an order?',
        anser: 'The ordering process for corporate and personal accounts is the same. You can modify or cancel your order at any time. Refunds for corporate account orders will be returned via the original payment method after cancellation.',
      },
      {
        title: 'Does Keeta support scheduled deliveries at specific times?',
        anser: 'Sorry, Keeta does not support scheduled delivery services at this time.',
      },
      {
        title: 'Does Keeta support corporate group orders?',
        anser: 'Yes, if you have such a need, please contact us. Keeta\'s professional corporate service team will provide you with a tailored group order solution.',
      },
    ],
  },
  {
    title: 'Payment and Allowance Usage Issues',
    subQuestions: [
      {
        title: 'What payment methods are supported for corporate accounts? Is cash payment supported?',
        anser: 'Corporate accounts support the same payment methods as personal accounts, including credit cards, PayMe, Alipay HK, Octopus, etc., but cash payment is not supported.<br/>Additionally, corporate accounts can use corporate payments or corporate wallet payments, ensuring you can use your exclusive corporate allowance balance for purchases.',
      },
      {
        title: 'Why did I receive a notification about a successful allowance recharge but can\'t see the wallet option?',
        anser: 'Please check if your Keeta app is updated to the latest version.',
      },
      {
        title: 'Why don\'t I see the wallet payment option on the order submission page?',
        anser: 'If your wallet balance is 0, you can only select personal payment methods when placing an order and cannot use wallet payments.',
      },
      {
        title: 'Can corporate allowances be withdrawn?',
        anser: 'You can directly use allowances to place orders on Keeta, but withdrawal is not currently supported.',
      },
      {
        title: 'When will corporate allowances expire?',
        anser: 'According to your company\'s management rules, each meal allowance has a specific validity period. Once expired, the allowance can no longer be used. You can check your email for the [Your Corporate Meal Allowance Has Been Credited] email, which specifies the validity period of the allowance. Relevant information is also available in the wallet to remind you to use the allowance before the expiration date.',
      },
    ],
  },
  {
    title: 'Technical Support Issues',
    subQuestions: [
      {
        title: 'Why can\'t I log in to my corporate account?',
        anser: 'Corporate accounts are linked to your corporate email. Please ensure you are using the correct email address to log in. If you receive a message stating that your corporate email is no longer in use, we recommend registering again with valid information.',
      },
    ],
  },
  {
    title: 'Other FAQs',
    subQuestions: [
      {
        title: 'Can I place orders via the web?',
        anser: 'Sorry, Keeta does not currently support ordering through the web.',
      },
      {
        title: 'How can I contact customer support? What are the service hours?',
        anser: 'If you have questions or need assistance with Keeta\'s services, you can contact Keeta through the following methods:<br/>1. Go to the [Account] section on the homepage, click [Customer Service Center], and follow the instructions to find answers to your questions. We provide 24/7 support with dedicated staff to assist you. (Highly Recommended)<br/>2. Call the corporate service hotline (service hours: 8 AM–11 PM daily): +852 21079500 (Corporate Exclusive)<br/>3. Email customer support (response time: 3–5 business days): myservice_customer@mykeeta.com',
      },
    ],
  },
]
const questionsZh: IQuestion[] = [
  {
    title: '賬戶與註冊問題',
    subQuestions: [
      {
        title: '如何註冊並創建企業賬戶？',
        anser: '若您有需求註冊並創建您的企業賬戶，請點擊【聯繫我們】，我們將為您提供企業賬戶開通服務及相關指引。',
      },
      {
        title: '企業賬戶管理員如何管理所屬企業的賬戶？',
        anser: '企業賬戶管理員可以在企業後台進行公司郵箱的批量註冊、批量管理，以及管理企業賬戶的各類信息等。',
      },
      {
        title: '我是否可以同時使用多個賬戶（個人賬戶和企業賬戶）？',
        anser: '我們支持您同時使用多個賬戶，但需要您區分登錄不同賬戶使用的登錄信息。同一手機號僅能綁定在一個賬戶內，若您已通過手機號碼註冊個人賬戶，在使用企業賬戶綁定手機號碼時，會提示此次綁定/換綁失敗。<br/>因此我們建議您通過企業郵箱註冊和登錄企業郵箱，通過手機號碼和其他信息登錄您的個人賬戶，但當您下單時，你可正常輸入你的手機號，便於送遞員與你取得聯繫。',
      },
    ],
  },
  {
    title: '訂單與配送問題',
    subQuestions: [
      {
        title: '如何下單？可以修改或取消訂單嗎？',
        anser: '企業賬號和個人賬號的下單流程保持一致，您可以隨時修改或取消您的訂單。您的企業賬戶訂單退款會在訂單取消後原路退回。',
      },
      {
        title: '是否支持特定時間的預約配送？',
        anser: '抱歉，目前keeta不支持預約配送服務。',
      },
      {
        title: '是否支持企業團體訂單？',
        anser: '支持，若您有需求，请联系我们，Keeta專業的企業服務團隊将为您提供專屬的團體訂單服務方案。',
      },
    ],
  },
  {
    title: '支付與津貼使用問題',
    subQuestions: [
      {
        title: '企業賬戶支持哪些支付方式？是否支持現金支付？',
        anser: '企業賬戶的支持支付方式與個人賬戶保持一致，支持包括信用卡、Payme、Alipay HK、八達通等多種付款方式，但不支持現金支付；<br/>此外，企業賬戶還可使用企業支付或者企業錢包支付，以保證您可使用您的專屬企業津貼額度進行消費。',
      },
      {
        title: '為什麼我已經收到了津貼充值成功的提醒，但看不到錢包入口？',
        anser: '您可以檢查您的Keeta App是否更新至最新版本。',
      },
      {
        title: '為什麼我在提交訂單頁面，看不到使用錢包支付的選項？',
        anser: '當您的錢包餘額為0，在提交訂單頁面您只能選擇個人支付方式進行落單，無法使用錢包支付。',
      },
      {
        title: '企業津貼支持提現嗎？',
        anser: '您可以直接使用津貼在Keeta下單，但目前暫不支持提現。',
      },
      {
        title: '企業津貼什麼時候會過期？',
        anser: '根據您所在企業的管理規則，每筆餐飲津貼都會有指定的有效期限，當有效期后該筆津貼將會過期，您將無法繼續使用。您可以在您的郵箱查看【您的企業餐飲津貼已到賬】郵件，郵件中說明了該筆津貼具體的有效期限。我們在錢包內亦會有相應內容，提醒您在有效期限前完成津貼使用。',
      },
    ],
  },
  {
    title: '技術支援問題',
    subQuestions: [
      {
        title: '為什麼我無法登錄企業賬戶？',
        anser: '企業賬戶與您的企業郵箱綁定，請確認您是否使用了正確的郵箱地址進行登錄。若提示由於您所提供的企業郵箱已經暫停使用，建議您重新使用有效信息重新註冊。',
      },
    ],
  },
  {
    title: '其他常見問題',
    subQuestions: [
      {
        title: '我是否可以使用網頁端進行點餐？',
        anser: '抱歉，目前keeta不支持通過網頁點餐。',
      },
      {
        title: '如何聯繫客戶支援？支援的服務時間是什麼時候？',
        anser: '如果您對Keeta提供的相關服務有疑問或者需要幫助，您可以通過以下方式聯繫Keeta：<br/>1）選擇首頁的【賬戶】，點擊【客戶服務中心】根據相關指引查詢相關問題，我們將會7*24小時第一時間對您的問題進行解答和協助「強力推薦」<br/>2）撥打企業服務熱線電話（服務時間為每日8-23點）：+852-21759920「企業專屬」<br/>3）發郵件到客服郵箱（約3-5個工作日回復）：myservice_customer@mykeeta.com',
      },
    ],
  },
]

const questionDataMap = {
  'zh': questionsZh,
  'zh-HK': questionsZh,
  'en': questionsEn,
}

export function useQuestionData () {
  const language = useLang()
  // @ts-ignore
  const questionData = (questionDataMap[language] || questionDataMap['en']) as IQuestion[]
  return questionData
}
